import { useLingui as useLinguiI18n } from "@lingui/react";
import { useLingui } from "@lingui/react/macro";
import { ChevronsRightIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  Sidebar,
  SIDEBAR_WIDTH,
  SidebarFooter,
  useSidebar,
} from "@/components/ui-kit/base/sidebar";
import { cn } from "@/utils/shadcn-ui";
import { useShortcut } from "@/utils/use-shortcut";

import { AppModel } from "../../app-model";
import {
  keyboardFocus,
  LevelTwoMenu,
  NavFlyoutProvider,
  NavTooltip,
  useActivePathname,
} from "../menu/menu-list";
import { buildMenu } from "../menu/nav-menu";
import { findActiveSection, RailMenu } from "../menu/rail-menu";

/**
 * Two-level navigation. Level 1 is an icon rail that never leaves; level 2 is a
 * panel positioned *inside* the sidebar, over the level-1 labels, so opening a
 * patient's ~20 pages never widens the sidebar or resizes the page — the problem
 * the single nested list had, where global destinations got pushed out of reach.
 *
 * Both levels share one collapsed state, and neither ever unmounts the other's
 * contents: the panel narrows to its own icon strip rather than sliding away, so
 * a patient's pages cannot go missing. While a panel is open the sidebar's total
 * is rail + panel — 17rem expanded, 7rem collapsed. Docking a collapsed panel to
 * the right of an expanded rail instead would make *collapsing* it widen the
 * sidebar to 19rem, the opposite of what the control is for.
 */
export const AppSidebar = observer(function AppSidebar({
  app,
}: {
  app: AppModel;
}) {
  const { i18n } = useLinguiI18n();
  const { state, isMobile, toggleSidebar, setOpenMobile } = useSidebar();
  const pathname = useActivePathname();
  // The current patient's pages hang off List of Patients in the shared tree,
  // but they are not that page's: a patient is reached from the list, a waiting
  // room or search alike. So the rail gets the list on its own — no section is
  // marked and no arrow drawn while you are on a patient — and the panel takes
  // the patient's pages directly.
  const [patients, ...sections] = buildMenu(app, useLocation().pathname, i18n);
  const railItems = [{ ...patients, children: undefined }, ...sections];

  const activeSection = findActiveSection(railItems, pathname);
  const sectionPages = (
    (activeSection ? activeSection.children : patients.children) ?? []
  ).filter((c) => c.show !== false);
  const hasLevels = sectionPages.length > 0;

  // What the panel renders: the section's pages while it is open, and the last
  // ones it showed while it closes. Leaving a section empties `sectionPages` on
  // the very first frame, so without this the panel slid away blank. The URL
  // is kept with them, as the live one no longer points into the list, and the
  // rows' highlights — a closed branch's parent row included — follow it.
  const panelContent = useRef({ pages: sectionPages, pathname });
  if (hasLevels) {
    panelContent.current = { pages: sectionPages, pathname };
  }

  // One collapsed state for both levels, the rail's own: collapsing the panel
  // and then leaving the section keeps the rail collapsed, and a collapsed rail
  // opens a section's pages as a strip.
  const collapsed = state === "collapsed" && !isMobile;
  // Open whenever the section has pages, full stop. The collapsed state narrows
  // it; nothing hides it.
  const panelOpen = hasLevels;
  // Two different reasons to hide the same rail labels: the panel covers them,
  // or the rail narrowed past them. Either way they fade rather than unmount, so
  // the icons beside them never move.
  const labelsHidden = panelOpen || collapsed;

  // Bound here rather than in SidebarProvider (where shadcn puts it) so it sits
  // beside the footer button and the double-click, which do the same.
  // Skipped while typing: ⌘B is bold in the app's rich-text editors, and
  // resizing the nav is never worth swallowing that keystroke.
  useShortcut("b", toggleSidebar, { skipWhileTyping: true });

  /**
   * Double-clicking the sidebar's own surface collapses or expands it — the
   * footer button without having to reach for the footer.
   *
   * Only on empty surface: a double-click that lands on a row, an arrow or the
   * collapse control has already done that control's job (twice), and collapsing
   * the sidebar out from under it would be a second, unasked-for action. Left
   * out on mobile, where the drawer has no collapsed state to toggle into.
   *
   * Bound to the level-2 panel as well as the rail.
   */
  const toggleOnEmptySurface = (event: React.MouseEvent) => {
    if (isMobile) return;
    const onControl = (event.target as HTMLElement).closest(
      "a, button, input, select, textarea, [role='button']",
    );
    if (onControl) return;
    toggleSidebar();
  };

  // On mobile the sidebar is a drawer over the page, so following a link must
  // close it or the page you navigated to stays hidden behind it. Keyed on the
  // click rather than the route, so a link to the page you are already on
  // closes it too. Expand chevrons aren't anchors, so they leave it open.
  const closeDrawerOnNav = (event: React.MouseEvent) => {
    if (isMobile && (event.target as HTMLElement).closest("a")) {
      setOpenMobile(false);
    }
  };

  return (
    <NavFlyoutProvider>
      <Sidebar
        collapsible="icon"
        // While a panel is open the contents decide the width, not the rail's
        // icon width — otherwise collapsing would clip the panel away, which is
        // the bug this replaces.
        width={
          panelOpen
            ? collapsed
              ? SIDEBAR_WIDTH_PANEL_COLLAPSED
              : SIDEBAR_WIDTH
            : undefined
        }
      >
        {/* Pinned at the expanded width so its contents never re-flow while the
          sidebar animates — labels would otherwise squeeze as it narrows. Set
          from the constant rather than `--sidebar-width`, which now tracks the
          sidebar's total and drops to 7rem with the panel collapsed.
          No background of its own: the rail keeps one surface in every state,
          inherited from the sidebar. */}
        <div
          className="absolute inset-0 flex flex-col"
          style={{ width: SIDEBAR_WIDTH }}
          onClick={closeDrawerOnNav}
          onDoubleClick={toggleOnEmptySurface}
        >
          <div className="flex-1 overflow-x-hidden overflow-y-auto px-2 pt-2.5 pb-1">
            <RailMenu
              items={railItems}
              activeSection={activeSection}
              labelsHidden={labelsHidden}
              collapsed={collapsed}
            />
          </div>

          <SidebarFooter className="border-sidebar-border h-12 flex-none justify-center border-t p-0">
            <Toggle
              collapsed={collapsed}
              labelHidden={labelsHidden}
              onToggle={toggleSidebar}
            />
          </SidebarFooter>
        </div>

        {/* Always mounted, even for a section with no pages: mounting it already
          open — which is what entering a patient does — gives the browser no
          previous value to animate from, so the panel would appear instantly
          instead of sliding in. */}
        <div
          data-slot="level-two-panel"
          data-open={panelOpen}
          data-collapsed={collapsed}
          onClick={closeDrawerOnNav}
          onDoubleClick={toggleOnEmptySurface}
          className={cn(
            "border-sidebar-border absolute top-0 bottom-0 left-14 z-10 flex flex-col overflow-hidden border-l",
            // 13.5rem, and SIDEBAR_WIDTH is the rail's 3.5rem plus this — see
            // the note there. Sized by its longest label in any shipped locale:
            // the row spends 48px of this on the 8px indent, the 3px marker,
            // `pl-[7px]`, the 20px icon box, its 6px gap and `pr-3`, leaving
            // 168px for text against the 165px Italian's "Gestione del
            // monitoraggio" measures (English's "Monitoring management" is 148px).
            // Collapsed it is a second 3.5rem icon strip, so the sidebar totals
            // 7rem and the content area reclaims the difference.
            collapsed ? "w-14" : "w-54",
            // Where it hides depends on the rail. Expanded, it slides out past
            // the sidebar's right edge, which clips it. Collapsed, it tucks behind
            // the rail's icon column instead: shifted a full width left and
            // clipped by the same amount from its left, so its visible left edge
            // stays on the rail's edge while its right edge travels with the
            // sidebar's own as that narrows by the strip's 3.5rem — it shrinks
            // in behind the first level. The -40px right inset keeps the shadow
            // on the moving edge.
            // `visibility` rides along so the hidden panel stays hidden while the
            // rail itself collapses or expands: that swaps it between the two
            // parked positions, and it would otherwise sweep across the sidebar.
            // Transitioned on the way out only, so a hide keeps the panel visible
            // until its slide ends. A show sets it at once: transitioned, a
            // discrete `visibility` still reads `hidden` on the first frame, and
            // with the rail collapsed the sidebar is already widening then, so
            // the section's active row showed past the rail before the panel
            // covered it.
            // The slide is written as `transform` on both sides, and `transform`
            // is what the transition names. Tailwind v4's `translate-x-*` sets the
            // *separate* `translate` property instead, so pairing those with a
            // `transition-[transform,…]` list transitions a property nothing
            // changes and the panel jumps — which is exactly what it did.
            // `width` is in the list so narrowing rides the same curve as the
            // sidebar container's own `transition-[width]`, rather than snapping
            // while the container animates around it.
            "bg-nav-panel shadow-[20px_0_40px_-28px_rgba(45,41,38,.55)] duration-280 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none",
            panelOpen
              ? "pointer-events-auto visible transition-[transform,clip-path,width] [clip-path:inset(0_-40px_0_0)] [transform:translateX(0)]"
              : cn(
                  "pointer-events-none invisible transition-[transform,clip-path,width,visibility]",
                  collapsed
                    ? "[clip-path:inset(0_-40px_0_100%)] [transform:translateX(-100%)]"
                    : "[clip-path:inset(0_-40px_0_0)] [transform:translateX(100%)]",
                ),
          )}
        >
          {/* Pinned at the panel's expanded width — the same `w-54` the panel
            takes when open, and it must stay in step — so the rows inside never
            re-flow while it animates: labels fade and slide out instead of
            squeezing, and the selected row keeps its inset and its icon column.
            The overflow above clips whatever hangs past the strip. This is the
            rail's own arrangement one level down; like the rail, the vertical
            scrollbar is off-strip while collapsed.

            pt-[13.5px] centres the first 33px row on the rail's first row: its
            list starts at pt-2.5 and the row is h-10, so its centre is at 30px,
            and 30 − 33/2 = 13.5. */}
          <div className="min-h-0 w-54 flex-1 overflow-x-hidden overflow-y-auto pt-[13.5px] pb-4">
            <LevelTwoMenu
              items={panelContent.current.pages}
              pathname={panelContent.current.pathname}
              collapsed={collapsed}
            />
          </div>
        </div>

        {/* The rail's edge while a collapsed panel slides out from behind it. The
          panel's own `border-l` is on its leftmost pixel, which the slide's
          clip uncovers last, so the edge would only appear once the slide was
          done. This one stands still: shown at once on open, and — through a
          `visibility` transition on the way out only, as on the panel — kept
          until the panel has finished tucking away. Expanded, the panel comes
          in from the right with its border on its leading edge, so this stays
          out of the way. */}
        <div
          aria-hidden
          className={cn(
            "bg-sidebar-border pointer-events-none absolute top-0 bottom-0 left-14 z-10 w-px motion-reduce:transition-none",
            panelOpen && collapsed
              ? "visible"
              : "invisible transition-[visibility] duration-280",
          )}
        />
      </Sidebar>
    </NavFlyoutProvider>
  );
});

/** Rail 3.5rem + the panel's own icon strip, 3.5rem. */
const SIDEBAR_WIDTH_PANEL_COLLAPSED = "7rem";

/** Footer row: the sidebar's own collapse control. */
function Toggle({
  collapsed,
  labelHidden,
  onToggle,
}: {
  /** Whether the sidebar is collapsed — both levels share the one state. */
  collapsed: boolean;
  /** Same two reasons as the rail labels: panel covering, or rail collapsed. */
  labelHidden: boolean;
  onToggle: () => void;
}) {
  const { t } = useLingui();
  // One string for the accessible name, the tooltip and the visible label, so
  // the button is named the same way however you meet it. "Menu" whether or
  // not a panel is open: both levels share the one collapsed state, so a press
  // always moves the whole menu, not just the open section's pages.
  const label = collapsed ? t`Expand menu` : t`Collapse menu`;
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={(event) => keyboardFocus(event) && setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={label}
        aria-keyshortcuts="Meta+B Control+B"
        className="text-sidebar-foreground/80 hover:text-sidebar-accent-foreground flex h-12 w-full cursor-pointer items-center gap-3 border-none bg-transparent px-[18px] text-[13px] transition-colors duration-140 ease-[ease]"
      >
        <ChevronsRightIcon
          className={cn(
            "size-[17px] flex-none transition-transform duration-280 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none",
            !collapsed && "rotate-180",
          )}
          strokeWidth={1.8}
        />
        <span
          className={cn(
            // Exactly the rail labels' treatment, so the whole sidebar's text
            // leaves as one movement rather than the footer fading on its own
            // timing. The slide also carries it clear of the rail, so nothing
            // peeks out from under the level-2 panel.
            "whitespace-nowrap transition-[opacity,transform] duration-280 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none",
            labelHidden && "opacity-0 [transform:translateX(-6px)]",
          )}
        >
          {label}
        </span>
      </button>

      {/* Same gate as the rail rows': whenever the label is unreadable, not
          only when the rail is the reason. The name has to follow that, too —
          hardcoding "Expand" was only right in the one case this used to
          cover. Starts at the column's left edge, so 56px reaches the rail's
          edge. */}
      {labelHidden && (
        <NavTooltip label={label} open={hovered || focused} width="w-14" />
      )}
    </div>
  );
}
